/**
 * JobAttachments — reusable attachments section for jobs.
 *
 * Files live in persistent object storage and are streamed through the
 * authenticated backend endpoint /api/jobs/attachments/{id}/file.
 *
 * Exports:
 *   default JobAttachments({ jobId, tokenKey })  — live list + upload (existing jobs)
 *   PendingAttachmentsPicker({ files, onChange }) — local picker for not-yet-saved jobs
 *   uploadJobAttachments(jobId, files, opts)      — batch uploader → { uploaded, failed }
 *   AttachmentLightbox                             — image preview dialog
 *   attachmentUrl / openAttachment / downloadAttachment
 *   fileUrl / isImage / formatFileSize / validateAttachmentFile — shared helpers
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Paperclip, Trash2, FileText, FileSpreadsheet, Loader2, Upload, X,
  Download, Pencil, AlertTriangle, RotateCcw, Image as ImageIcon, CheckCircle2,
} from "lucide-react";
import { API } from "../../App";

// NOTE: derived from env, not `API`, because this module is imported through a
// cycle (App → pages → CustomerModal → here) — `API` is in TDZ at module eval.
const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const MAX_SIZE = 20 * 1024 * 1024;
const ACCEPTED_EXTS = ["jpg", "jpeg", "png", "heic", "heif", "webp", "gif", "pdf", "doc", "docx", "xls", "xlsx", "txt"];
const IMAGE_EXTS = ["jpg", "jpeg", "png", "heic", "heif", "webp", "gif"];
const ACCEPT_ATTR = ACCEPTED_EXTS.map((e) => `.${e}`).join(",");

const getExt = (name) => {
  if (!name || !name.includes(".")) return "";
  return name.split(".").pop().toLowerCase();
};

export const isImage = (att) => {
  if ((att?.content_type || "").startsWith("image/")) return true;
  return IMAGE_EXTS.includes(att?.ext || getExt(att?.file_name));
};
const attExt = (att) => (att?.ext || getExt(att?.file_name) || "").toLowerCase();
const isPdf = (att) => attExt(att) === "pdf" || att?.content_type === "application/pdf";
const isTxt = (att) => attExt(att) === "txt";
export const isMissing = (att) => att?.status === "missing";

// Authenticated file URL. Tokens go via ?auth= because <img>/window.open
// cannot send Authorization headers — the backend validates them identically.
export const attachmentUrl = (att, { tokenKey = "token", download = false, preview = false } = {}) => {
  const token = localStorage.getItem(tokenKey) || "";
  const params = new URLSearchParams();
  if (token) params.set("auth", token);
  if (download) params.set("download", "1");
  if (preview) params.set("preview", "1");
  return `${BACKEND_ORIGIN}/api/jobs/attachments/${att?.attachment_id}/file?${params.toString()}`;
};

// Legacy alias (older imports)
export const fileUrl = (att) => attachmentUrl(att);

export const openAttachment = (att, tokenKey = "token") => {
  if (isMissing(att)) { toast.error("This file is no longer available"); return; }
  if (isPdf(att) || isTxt(att) || isImage(att)) {
    window.open(attachmentUrl(att, { tokenKey, preview: isImage(att) }), "_blank", "noopener");
  } else {
    downloadAttachment(att, tokenKey);
  }
};

export const downloadAttachment = (att, tokenKey = "token") => {
  if (isMissing(att)) { toast.error("This file is no longer available"); return; }
  const a = document.createElement("a");
  a.href = attachmentUrl(att, { tokenKey, download: true });
  a.download = att.file_name || "";
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const formatFileSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const validateAttachmentFile = (file) => {
  const ext = getExt(file.name);
  if (!ACCEPTED_EXTS.includes(ext)) {
    return `${file.name}: unsupported type. Allowed: JPG, PNG, HEIC, WEBP, GIF, PDF, DOC, DOCX, XLS, XLSX, TXT`;
  }
  if (file.size > MAX_SIZE) return `${file.name}: exceeds the 20MB size limit`;
  if (file.size === 0) return `${file.name}: file is empty`;
  return null;
};

// Batch uploader (used after job creation). Never throws for individual
// failures — returns { uploaded: [...], failed: [{ file, error }] }.
export const uploadJobAttachments = async (jobId, files, { tokenKey = "token", onProgress } = {}) => {
  const token = localStorage.getItem(tokenKey);
  const uploaded = [];
  const failed = [];
  for (let i = 0; i < files.length; i++) {
    const fd = new FormData();
    fd.append("file", files[i]);
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/attachments`, fd, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          onProgress?.(i, pct, files[i]);
        },
      });
      uploaded.push(res.data);
    } catch (e) {
      failed.push({ file: files[i], error: e.response?.data?.detail || "Upload failed" });
    }
  }
  return { uploaded, failed };
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
};

// ─── Image lightbox ────────────────────────────────────────────────────────
export const AttachmentLightbox = ({ att, tokenKey = "token", onClose }) => {
  if (!att) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl p-3 sm:p-4" data-testid="attachment-lightbox">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-6">
            <span className="truncate text-sm font-medium">{att.file_name}</span>
            <Button
              size="sm" variant="outline" className="h-7 text-xs shrink-0"
              onClick={() => downloadAttachment(att, tokenKey)}
              data-testid="lightbox-download-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download
            </Button>
          </DialogTitle>
        </DialogHeader>
        <img
          src={attachmentUrl(att, { tokenKey, preview: true })}
          alt={att.file_name}
          className="max-h-[72vh] w-auto mx-auto rounded-lg object-contain bg-black/5"
          data-testid="lightbox-image"
        />
      </DialogContent>
    </Dialog>
  );
};

// ─── File type icon ────────────────────────────────────────────────────────
const TypeIcon = ({ att, className = "w-8 h-8" }) => {
  const ext = attExt(att);
  if (["xls", "xlsx"].includes(ext)) return <FileSpreadsheet className={`${className} text-emerald-500/80`} />;
  if (["doc", "docx"].includes(ext)) return <FileText className={`${className} text-blue-500/80`} />;
  if (ext === "txt") return <FileText className={`${className} text-gray-400`} />;
  if (isImage(att)) return <ImageIcon className={`${className} text-cyan-500/80`} />;
  return <FileText className={`${className} text-red-500/70`} />;
};

// ─── Tile ──────────────────────────────────────────────────────────────────
const AttachmentTile = ({ att, tokenKey, onDelete, onRename, onPreview, deleting }) => {
  const [imgError, setImgError] = useState(false);
  const missing = isMissing(att);

  const handleOpen = () => {
    if (missing) { toast.error("This file is no longer available"); return; }
    if (isImage(att) && !imgError) onPreview(att);
    else openAttachment(att, tokenKey);
  };

  return (
    <div
      className={`rounded-lg border border-border bg-muted/30 overflow-hidden group relative ${missing ? "opacity-70" : ""}`}
      data-testid={`attachment-item-${att.attachment_id}`}
    >
      <button
        type="button"
        onClick={handleOpen}
        className="w-full text-left"
        disabled={missing}
        data-testid={`attachment-open-${att.attachment_id}`}
      >
        {missing ? (
          <div className="w-full h-20 flex flex-col items-center justify-center gap-1 bg-muted/50" data-testid={`attachment-missing-${att.attachment_id}`}>
            <AlertTriangle className="w-6 h-6 text-amber-500/70" />
            <span className="text-[10px] text-muted-foreground font-medium">File unavailable</span>
          </div>
        ) : isImage(att) && !imgError ? (
          <img
            src={attachmentUrl(att, { tokenKey, preview: true })}
            alt={att.file_name}
            className="w-full h-20 object-cover bg-muted"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-20 flex items-center justify-center bg-muted/40">
            <TypeIcon att={att} />
          </div>
        )}
        <div className="px-2 pt-1.5">
          <p className="text-[11px] font-medium text-foreground truncate">{att.file_name}</p>
          <p className="text-[10px] text-muted-foreground truncate">
            {formatFileSize(att.size)}{att.created_at ? ` · ${fmtDate(att.created_at)}` : ""}
          </p>
          {att.uploaded_by_name && (
            <p className="text-[10px] text-muted-foreground/70 truncate">by {att.uploaded_by_name}</p>
          )}
        </div>
      </button>
      <div className="flex items-center gap-0.5 px-1.5 pb-1.5 pt-0.5">
        <button
          type="button"
          onClick={() => downloadAttachment(att, tokenKey)}
          disabled={missing}
          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40"
          title="Download"
          data-testid={`attachment-download-${att.attachment_id}`}
        >
          <Download className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onRename(att)}
          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
          title="Rename"
          data-testid={`attachment-rename-${att.attachment_id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        {att.can_delete && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(att)}
            disabled={deleting}
            className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 ml-auto"
            title="Delete"
            data-testid={`attachment-delete-${att.attachment_id}`}
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────
const JobAttachments = ({ jobId, tokenKey = "token" }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Upload queue: [{ qid, file, status: 'uploading'|'failed'|'done', pct, error }]
  const [queue, setQueue] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [lightboxAtt, setLightboxAtt] = useState(null);
  const [renaming, setRenaming] = useState(null); // { att, name }
  const inputRef = useRef(null);

  const busy = queue.some((q) => q.status === "uploading");

  const authCfg = useCallback(() => {
    const token = localStorage.getItem(tokenKey);
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  }, [tokenKey]);

  useEffect(() => {
    if (!jobId) return;
    let active = true;
    setLoading(true);
    axios.get(`${API}/jobs/${jobId}/attachments`, authCfg())
      .then((res) => active && setAttachments(Array.isArray(res.data) ? res.data : []))
      .catch(() => active && setAttachments([]))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [jobId, authCfg]);

  const updateQ = (qid, patch) =>
    setQueue((prev) => prev.map((q) => (q.qid === qid ? { ...q, ...patch } : q)));

  const uploadOne = async (entry) => {
    updateQ(entry.qid, { status: "uploading", pct: 0, error: null });
    const fd = new FormData();
    fd.append("file", entry.file);
    try {
      const token = localStorage.getItem(tokenKey);
      const res = await axios.post(`${API}/jobs/${jobId}/attachments`, fd, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          updateQ(entry.qid, { pct });
        },
      });
      setAttachments((prev) => [res.data, ...prev]);
      updateQ(entry.qid, { status: "done", pct: 100 });
      setTimeout(() => setQueue((prev) => prev.filter((q) => q.qid !== entry.qid)), 1500);
    } catch (e) {
      updateQ(entry.qid, { status: "failed", error: e.response?.data?.detail || "Upload failed — please retry" });
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (inputRef.current) inputRef.current.value = "";
    if (files.length === 0) return;
    const valid = [];
    files.forEach((f) => {
      const err = validateAttachmentFile(f);
      if (err) { toast.error(err); return; }
      // Skip files already queued (double-click / duplicate selection guard)
      const dup = queue.some((q) => q.file.name === f.name && q.file.size === f.size && q.status !== "done");
      if (dup) { toast.info(`${f.name} is already uploading`); return; }
      valid.push(f);
    });
    if (valid.length === 0) return;
    const entries = valid.map((f) => ({
      qid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f, status: "uploading", pct: 0, error: null,
    }));
    setQueue((prev) => [...prev, ...entries]);
    let ok = 0;
    for (const entry of entries) {
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(entry);
      ok += 1;
    }
    // Success toast only for the ones that made it (failed ones stay visible with Retry)
    void ok;
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Delete "${att.file_name}"? This cannot be undone.`)) return;
    setDeletingId(att.attachment_id);
    try {
      await axios.delete(`${API}/jobs/attachments/${att.attachment_id}`, authCfg());
      setAttachments((prev) => prev.filter((a) => a.attachment_id !== att.attachment_id));
      toast.success("Attachment deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete attachment");
    } finally {
      setDeletingId(null);
    }
  };

  const handleRenameSave = async () => {
    const { att, name } = renaming || {};
    if (!att || !name?.trim()) return;
    try {
      const res = await axios.patch(`${API}/jobs/attachments/${att.attachment_id}`, { file_name: name.trim() }, authCfg());
      setAttachments((prev) => prev.map((a) => (a.attachment_id === att.attachment_id ? { ...a, file_name: res.data.file_name } : a)));
      toast.success("Renamed");
      setRenaming(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename");
    }
  };

  return (
    <div className="rounded-xl bg-muted/40 border border-border p-3" data-testid="job-attachments-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Paperclip className="w-3 h-3" /> Attachments
          {!loading && <span className="text-foreground">· {attachments.length}</span>}
        </p>
        <Button
          type="button" size="sm" variant="outline"
          className="h-7 text-xs"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid="attachment-upload-btn"
        >
          {busy ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Upload className="w-3 h-3 mr-1.5" />}
          Upload
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="attachment-upload-input"
        />
      </div>

      {/* Upload queue — per-file progress / retry */}
      {queue.length > 0 && (
        <div className="mb-2 space-y-1.5" data-testid="attachment-upload-progress">
          {queue.map((q) => (
            <div key={q.qid} className="rounded-md border border-border bg-background/60 px-2 py-1.5" data-testid={`upload-entry-${q.status}`}>
              <div className="flex items-center gap-2">
                {q.status === "uploading" && <Loader2 className="w-3 h-3 animate-spin text-[#0046BE] shrink-0" />}
                {q.status === "done" && <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />}
                {q.status === "failed" && <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" />}
                <span className="text-[11px] text-foreground truncate flex-1">{q.file.name}</span>
                {q.status === "uploading" && <span className="text-[10px] text-muted-foreground">{q.pct}%</span>}
                {q.status === "done" && <span className="text-[10px] text-emerald-500 font-medium">Uploaded</span>}
                {q.status === "failed" && (
                  <>
                    <button
                      type="button"
                      onClick={() => uploadOne(q)}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-[#0046BE] hover:underline"
                      data-testid={`attachment-retry-${q.qid}`}
                    >
                      <RotateCcw className="w-3 h-3" /> Retry
                    </button>
                    <button
                      type="button"
                      onClick={() => setQueue((prev) => prev.filter((x) => x.qid !== q.qid))}
                      className="p-0.5 text-muted-foreground hover:text-foreground"
                      data-testid={`attachment-dismiss-${q.qid}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
              {q.status === "uploading" && (
                <div className="h-1 mt-1 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-[#FFCC00] rounded-full transition-all" style={{ width: `${q.pct}%` }} />
                </div>
              )}
              {q.status === "failed" && q.error && (
                <p className="text-[10px] text-red-500 mt-0.5 truncate">{q.error}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : attachments.length === 0 && queue.length === 0 ? (
        <p className="text-xs text-muted-foreground/70">
          No files yet. Upload photos or documents (JPG, PNG, HEIC, PDF, DOC, XLS, TXT — max 20MB each).
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {attachments.map((att) => (
            <AttachmentTile
              key={att.attachment_id}
              att={att}
              tokenKey={tokenKey}
              onDelete={handleDelete}
              onRename={(a) => setRenaming({ att: a, name: a.file_name || "" })}
              onPreview={setLightboxAtt}
              deleting={deletingId === att.attachment_id}
            />
          ))}
        </div>
      )}

      {/* Image lightbox */}
      <AttachmentLightbox att={lightboxAtt} tokenKey={tokenKey} onClose={() => setLightboxAtt(null)} />

      {/* Rename dialog */}
      {renaming && (
        <Dialog open onOpenChange={(o) => { if (!o) setRenaming(null); }}>
          <DialogContent className="max-w-sm" data-testid="attachment-rename-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm">Rename attachment</DialogTitle>
            </DialogHeader>
            <Input
              value={renaming.name}
              onChange={(e) => setRenaming((r) => ({ ...r, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") handleRenameSave(); }}
              autoFocus
              data-testid="attachment-rename-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setRenaming(null)}>Cancel</Button>
              <Button size="sm" className="bg-[#0046BE] hover:bg-[#0046BE]/90 text-white" onClick={handleRenameSave} data-testid="attachment-rename-save">
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Picker used inside the job form BEFORE the job exists — holds File objects
// locally; the form uploads them right after the job is created.
export const PendingAttachmentsPicker = ({ files, onChange }) => {
  const inputRef = useRef(null);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const valid = [];
    incoming.forEach((f) => {
      const err = validateAttachmentFile(f);
      if (err) { toast.error(err); return; }
      const dup = (files || []).some((x) => x.name === f.name && x.size === f.size);
      if (dup) { toast.info(`${f.name} is already added`); return; }
      valid.push(f);
    });
    if (valid.length) onChange([...(files || []), ...valid]);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-2" data-testid="pending-attachments-picker">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#FFCC00]">
          <Paperclip className="w-4 h-4" />
          <span className="text-sm font-medium">Attachments</span>
          {files?.length > 0 && <span className="text-xs text-muted-foreground">({files.length})</span>}
        </div>
        <Button
          type="button" size="sm" variant="outline" className="h-7 text-xs"
          onClick={() => inputRef.current?.click()}
          data-testid="pending-attachment-add-btn"
        >
          <Upload className="w-3 h-3 mr-1.5" /> Add files
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
          data-testid="pending-attachment-input"
        />
      </div>
      {files?.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="flex items-center gap-2 pl-1.5 pr-1 py-1 rounded-lg bg-muted/50 border border-border max-w-full"
              data-testid={`pending-attachment-${i}`}
            >
              {f.type.startsWith("image/") ? (
                <img src={URL.createObjectURL(f)} alt={f.name} className="w-7 h-7 rounded object-cover" />
              ) : (
                <FileText className="w-4 h-4 text-red-500/70 shrink-0" />
              )}
              <div className="min-w-0">
                <p className="text-[11px] text-foreground truncate max-w-[140px]">{f.name}</p>
                <p className="text-[10px] text-muted-foreground">{formatFileSize(f.size)}</p>
              </div>
              <button
                type="button"
                onClick={() => onChange(files.filter((_, idx) => idx !== i))}
                className="p-0.5 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                data-testid={`pending-attachment-remove-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground/70">
          Photos or documents (max 20MB each) — uploaded when the job is saved.
        </p>
      )}
    </div>
  );
};

export default JobAttachments;
